import { Trans, useTranslation } from 'react-i18next';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { isDecimalGreaterThan, parsePositiveDecimal } from '@shared/lib/parse-decimal';

interface ThresholdHintProps {
  thresholdAmount: string;
  /** Token the threshold is denominated in. Single-target mode is USDT; a direct rule uses its own asset. */
  assetCode?: string;
  totalBalance?: number;
  balanceAmount?: string | number;
}

export const ThresholdHint = ({
  thresholdAmount,
  assetCode = 'USDT',
  totalBalance,
  balanceAmount,
}: ThresholdHintProps) => {
  const { t } = useTranslation('cabinet');
  const threshold = parsePositiveDecimal(thresholdAmount);
  const asset = assetCode.trim() || 'USDT';

  if (!threshold.valid) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('withdrawal.auto.single.threshold.enter', { asset })}
      </p>
    );
  }

  const formatted = formatLedgerAmount(threshold.normalized, { assetLabel: asset });
  const comparable = balanceAmount ?? totalBalance;
  const above =
    comparable != null &&
    comparable !== '' &&
    isDecimalGreaterThan(String(comparable), threshold.normalized);

  if (above) {
    return (
      <p className="text-xs text-muted-foreground">
        <Trans
          t={t}
          i18nKey="withdrawal.auto.single.threshold.above"
          values={{ amount: formatted }}
          components={{
            amount: <span className="font-semibold tabular-nums text-foreground" />,
            strong: <strong className="font-semibold text-foreground" />,
          }}
        />
      </p>
    );
  }

  return (
    <p className="text-xs text-muted-foreground">
      <Trans
        t={t}
        i18nKey="withdrawal.auto.single.threshold.below"
        values={{ amount: formatted }}
        components={{
          amount: <span className="font-semibold tabular-nums text-foreground" />,
        }}
      />
    </p>
  );
};
